// Retro arcade SFX synthesized entirely with the Web Audio API.
// No binary audio assets required, keeps the repo self-contained,
// and the bleepy/noisy character actually suits the PS1-era brief.
export class GameAudio {
  private ctx: AudioContext;
  private master: GainNode;

  constructor() {
    this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.35;
    this.master.connect(this.ctx.destination);
  }

  resume() {
    if (this.ctx.state === 'suspended') this.ctx.resume();
  }

  private tone(freq: number, durationMs: number, type: OscillatorType, gainStart = 0.6) {
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.value = gainStart;
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + durationMs / 1000);
    osc.connect(gain);
    gain.connect(this.master);
    osc.start();
    osc.stop(this.ctx.currentTime + durationMs / 1000);
  }

  private noise(durationMs: number, gainStart = 0.5) {
    const bufferSize = this.ctx.sampleRate * (durationMs / 1000);
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    const gain = this.ctx.createGain();
    gain.gain.value = gainStart;
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + durationMs / 1000);
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 800;
    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);
    source.start();
  }

  shot(weaponCategory: string) {
    // Punchy noise burst + a low thump, tuned slightly per weapon class.
    this.noise(weaponCategory === 'sniper' ? 140 : 70, weaponCategory === 'shotgun' ? 0.7 : 0.5);
    this.tone(weaponCategory === 'sniper' ? 90 : 140, 90, 'square', 0.3);
  }

  hitMarker() {
    this.tone(1200, 60, 'square', 0.25);
    this.tone(1800, 50, 'square', 0.15);
  }

  targetExpire() {
    this.tone(220, 180, 'sawtooth', 0.2);
  }

  miss() {
    this.tone(160, 120, 'sawtooth', 0.2);
  }

  reload() {
    this.tone(400, 80, 'square', 0.15);
    setTimeout(() => this.tone(600, 100, 'square', 0.15), 120);
  }

  gameOver() {
    this.tone(500, 200, 'square', 0.3);
    setTimeout(() => this.tone(350, 300, 'square', 0.3), 180);
    setTimeout(() => this.tone(220, 500, 'square', 0.3), 400);
  }

  countdownTick() {
    this.tone(880, 80, 'square', 0.2);
  }
}
